import { useState, useEffect, useRef, createRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import icons
import { faChevronLeft, faChevronRight, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
// import image
import aiiaMachineFrame from "../../image/robotics/aiia_machine7.png";
import kioskGroup from "../../image/robotics/kiosk_group.png";
// import data
import aiiaRolesData from "../../data/content/robotics/aiiaRoles.json";
import aiiaModelsData from "../../data/content/robotics/aiiaModels.json";
import { DataKeys, DataStructure } from "../../types";
// import components
import { SpacingFrame } from "../display/Frame";
import { TitleSubtitle } from "../display/TextFormat";
import { ImageImport } from "../display/ImageFormat";
// import context
import { useLang, useScreenSize } from "../Context";
// import global var
import { maxWidthScreen, typedVocab } from "../GlobalVar";

const typedRoles : DataStructure = aiiaRolesData;
const typedModels : DataStructure = aiiaModelsData;

const ARROW_CONTENT_GAP_PX = 24;
const ARROW_BUTTON_SIZE_PX = 40;

/** Screen opening on aiia_machine7.png — same clip as Aiia hero */
const AIIA_KIOSK_SCREEN_CLIP =
    "left-[28.06%] top-[14.06%] h-[53.63%] w-[42.96%] overflow-hidden";

export function AiiaRolesSlideShow() {
    const { lang } = useLang();
    const { screenSize } = useScreenSize();
    const roleList = Object.keys(typedRoles);

    const [selRole, setSelRole] = useState<string>(roleList[0]);
    const [selData, setSelData] = useState<DataKeys | undefined>(undefined);
    const [avatarRole, setAvatarRole] = useState<string>(roleList[0]);
    const [avatarImage, setAvatarImage] = useState("");
    const [avatarColor, setAvatarColor] = useState<string>("");
    const [showArrows, setShowArrows] = useState<boolean>(false);
    const [isNext, setIsNext] = useState<boolean>(true);
    const contentRef = useRef<HTMLDivElement>(null);
    const avatarAnimatingRef = useRef(false);
    const pendingAvatarRoleRef = useRef<string | null>(null);
    const avatarRoleRef = useRef(avatarRole);
    avatarRoleRef.current = avatarRole;

    const AVATAR_SLIDE_MS = 450;

    useEffect(() => {
        if (selRole) {
            setSelData(typedRoles[selRole]);
        };
    }, [selRole]);

    useEffect(() => {
        const data = typedRoles[avatarRole];
        if (!data) return;
        setAvatarImage(require("../../image/robotics/" + data["image"]));
        setAvatarColor(data["color"]);
    }, [avatarRole]);

    function startAvatarSlide(nextRole: string) {
        const fromIdx = roleList.indexOf(avatarRoleRef.current);
        const toIdx = roleList.indexOf(nextRole);
        if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) {
            avatarAnimatingRef.current = false;
            return;
        }
        // Prefer explicit wrap direction when adjacent in loop; else by index
        const forward = (fromIdx + 1) % roleList.length === toIdx;
        const backward = (toIdx + 1) % roleList.length === fromIdx;
        setIsNext(forward ? true : backward ? false : toIdx > fromIdx);
        avatarAnimatingRef.current = true;
        setAvatarRole(nextRole);
    }

    function queueAvatarSlide(nextRole: string) {
        if (nextRole === avatarRoleRef.current && !avatarAnimatingRef.current) return;
        if (avatarAnimatingRef.current) {
            pendingAvatarRoleRef.current = nextRole;
            return;
        }
        startAvatarSlide(nextRole);
    }

    function onAvatarSlideComplete(definition: string | object) {
        if (definition !== "center") return;
        avatarAnimatingRef.current = false;
        const pending = pendingAvatarRoleRef.current;
        pendingAvatarRoleRef.current = null;
        if (pending && pending !== avatarRoleRef.current) {
            startAvatarSlide(pending);
        }
    }

    useEffect(() => {
        if (screenSize === "Small") {
            setShowArrows(false);
            return;
        }

        const updateArrowVisibility = () => {
            const contentEl = contentRef.current;
            if (!contentEl) {
                setShowArrows(false);
                return;
            }

            const rect = contentEl.getBoundingClientRect();
            const requiredGutter = ARROW_BUTTON_SIZE_PX + ARROW_CONTENT_GAP_PX;
            const leftGutter = rect.left;
            const rightGutter = window.innerWidth - rect.right;

            setShowArrows(leftGutter >= requiredGutter && rightGutter >= requiredGutter);
        };

        updateArrowVisibility();
        window.addEventListener("resize", updateArrowVisibility);

        const resizeObserver = new ResizeObserver(updateArrowVisibility);
        if (contentRef.current) {
            resizeObserver.observe(contentRef.current);
        }

        return () => {
            window.removeEventListener("resize", updateArrowVisibility);
            resizeObserver.disconnect();
        };
    }, [screenSize, selData]);

    const itemRefs = useRef<any[]>([]);
    useEffect(() => {
        itemRefs.current = Object.keys(typedRoles).map(() => createRef());
    }, [typedRoles]);

    function scrollToItem(index : any) {
        const itemRef = itemRefs.current[index];
        if (itemRef && itemRef.current) {
            itemRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "start",
            });
        }
    }

    function newSelection(newRole: string) {
        if (newRole === selRole) return;
        setSelRole(newRole);
        queueAvatarSlide(newRole);
        scrollToItem(roleList.indexOf(newRole));
    }

    let headers = Object.keys(typedRoles).map((i, index) => {
        let title = typedRoles[i]["label"][lang];
        let textStyle = selRole === i
                        ? "text-md text-nowrap text-teal-500 font-semibold group-hover:text-teal-500"
                        : "text-md text-nowrap text-grey-700 font-semibold group-hover:text-teal-500"
        return (
            <div onClick={() => newSelection(i)} ref={itemRefs.current[index]}
                className={ `${selRole === i && "w-fit h-fit border-b-2 border-b-teal-500"}` }
            >
                <div className="group w-fit cursor-pointer rounded-md border-2 border-transparent py-2 px-4
                                transition-all duration-300 hover:bg-grey-100/50">
                    <p className={ textStyle }>{ title }</p>
                </div>
            </div>
        )
    });

    function nextItem(next: boolean) {
        const currentIndex = roleList.indexOf(selRole);
        let direction = next ? 1 : -1;
        let nextIndex = (currentIndex + direction) % roleList.length;
        if (nextIndex < 0) {
            nextIndex = roleList.length - 1;
        }
        const nextRole = roleList[nextIndex];
        setSelRole(nextRole);
        queueAvatarSlide(nextRole);
        scrollToItem(nextIndex);
    }

    const arrowButtonClass =
        "pointer-events-auto absolute top-1/2 z-30 -translate-y-1/2 cursor-pointer rounded-full p-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500";

    return (
        <div className="relative w-full">
            <SpacingFrame spacing={48}>
                <div className="w-full grid grid-flow-col auto-cols-auto lg:gap-4 sm:gap-1 overflow-x-auto z-40 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:overflow-visible">
                    { headers }
                </div>
                { selData &&
                    <div ref={contentRef} className="relative w-full">
                        { showArrows &&
                            <>
                                <button
                                    type="button"
                                    className={`${arrowButtonClass} right-full`}
                                    style={{ marginRight: `${ARROW_CONTENT_GAP_PX}px` }}
                                    onClick={() => nextItem(false)}
                                    aria-label="Previous role"
                                >
                                    <FontAwesomeIcon className="text-xl text-teal-100" icon={faChevronLeft} />
                                </button>
                                <button
                                    type="button"
                                    className={`${arrowButtonClass} left-full`}
                                    style={{ marginLeft: `${ARROW_CONTENT_GAP_PX}px` }}
                                    onClick={() => nextItem(true)}
                                    aria-label="Next role"
                                >
                                    <FontAwesomeIcon className="text-xl text-teal-100" icon={faChevronRight} />
                                </button>
                            </>
                        }
                        <div className="flex w-full flex-col-reverse items-center gap-8 overflow-hidden lg:grid lg:grid-cols-5 lg:items-start lg:overflow-hidden">
                        <div className="col-span-3 w-full transition duration-500 z-40">
                            <SpacingFrame>
                                <p className="text-xl text-left font-semibold text-grey-800">{ selData["title"][lang] }</p>
                                <p className="text-left">
                                    <span className="font-semibold">{ typedVocab["Scenarios"][lang] }: </span>
                                    <span>{ selData["scenarios"][lang] }</span>
                                </p>
                                <p className="text-left">{ selData["description"][lang] }</p>
                                <CreateInfo data={ selData["content"] } />
                            </SpacingFrame>
                        </div>
                        <div className="col-span-2 relative flex w-full justify-center">
                            {/* Scale up ~110% (122% × 90%); pin to bottom; crop top blank */}
                            <div className="relative mx-auto h-[min(669px,85vw)] w-auto aspect-[1080/1501] shrink-0 overflow-hidden lg:h-[669px]">
                                <div className="absolute bottom-0 left-1/2 h-[110%] w-[110%] -translate-x-1/2">
                                    <img
                                        className="relative z-10 block h-full w-full pointer-events-none"
                                        src={aiiaMachineFrame}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <div
                                        className={`absolute z-20 bg-white ${AIIA_KIOSK_SCREEN_CLIP}`}
                                        aria-hidden="true"
                                    >
                                        <AnimatePresence initial={false} custom={isNext} mode="sync">
                                            <motion.img
                                                key={avatarRole}
                                                className="absolute bottom-0 right-0 h-[95%] w-auto max-w-[95%] object-contain object-right-bottom"
                                                src={avatarImage}
                                                alt=""
                                                custom={isNext}
                                                variants={{
                                                    enter: (next: boolean) => ({
                                                        x: next ? "110%" : "-110%",
                                                    }),
                                                    center: { x: 0 },
                                                    exit: (next: boolean) => ({
                                                        x: next ? "-110%" : "110%",
                                                    }),
                                                }}
                                                initial="enter"
                                                animate="center"
                                                exit="exit"
                                                transition={{ type: "tween", duration: AVATAR_SLIDE_MS / 1000, ease: "easeInOut" }}
                                                onAnimationComplete={onAvatarSlideComplete}
                                            />
                                        </AnimatePresence>
                                    </div>
                                    <div className="absolute left-[30.5%] top-[18%] z-30 flex h-5 w-[14%] min-w-[36px] max-w-[52px] flex-col gap-0.5 rounded-md rounded-br-none bg-white p-1 shadow-bubble lg:h-8 lg:w-[18%] lg:min-w-[56px] lg:max-w-[84px] lg:gap-1 lg:rounded-lg lg:p-1.5">
                                        <hr className="h-0.5 w-full rounded border-0 bg-grey-100" />
                                        <hr className="h-0.5 w-full rounded border-0 bg-grey-100" />
                                        <hr className="h-0.5 w-1/2 rounded border-0 bg-grey-100" />
                                    </div>
                                    <div
                                        className="absolute left-[30.5%] top-[24%] z-30 flex h-5 w-[11%] min-w-[30px] max-w-[40px] flex-col gap-0.5 rounded-md rounded-bl-none px-1 py-1.5 shadow-bubble transition duration-500 lg:top-[23.5%] lg:h-8 lg:w-[14%] lg:min-w-[48px] lg:max-w-[64px] lg:gap-1 lg:rounded-lg lg:px-1.5 lg:py-2"
                                        style={{ backgroundColor: avatarColor }}
                                    >
                                        <hr className="h-0.5 w-full rounded border-0 bg-grey-600" />
                                        <hr className="h-0.5 w-1/2 rounded border-0 bg-grey-600" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                }
            </SpacingFrame>
        </div>
    )
};

interface CreateInfoProps {
    data: DataStructure;
};
function CreateInfo({ data } : CreateInfoProps) {
    const { lang } = useLang();

    let showInfo = Object.keys(data).map((i) => {
        let info = data[i];
        let image = require("../../image/icons/" + info["icon"]);
        return (
            <div className="flex gap-4">
                <img className="mb-auto flex items-start justify-start" src={image} alt="" />
                <SpacingFrame spacing={12}>
                    <p className="text-md text-left font-bold text-teal-500">{ info["title"][lang] }</p>
                    <p className="text-sm text-left text-grey-400">{ info["text"][lang] }</p>
                </SpacingFrame>
            </div>
        )
    });

    return (
        <SpacingFrame>
            { showInfo }
        </SpacingFrame>
    )
};

export function AiiaHardwareModels() {
    const { lang } = useLang();
    const [selModel, setSelModel] = useState<string>("");

    function selectModel(model: string) {
        if (selModel === model) {
            setSelModel("");
        } else {
            setSelModel(model);
        };
    };

    let sideBar = Object.keys(typedModels).map((i) => {
        let data = typedModels[i];

        return (
            <motion.div onClick={() => selectModel(i)}
                className={`w-[90%] h-fit flex flex-col gap-2 border-l-4 py-0.5 px-3 cursor-pointer lg:m-0 sm:m-auto
                            ${ selModel === i ? "border-teal-500" : "border-teal-300 hover:bg-teal-300/40"} transition duration-500`}
                initial={{ opacity: 0 }}
                animate={selModel === i ? { height: "auto", opacity: 1 } : { height: "32px", opacity: 1 }}
                exit={{ height: "32px", opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                style={{ overflow: 'hidden' }}
            >
                <div className="flex">
                    <p className="w-full text-left text-lg font-semibold text-grey-800">{ data["title"][lang] }</p>
                    <div className="relative w-fit flex items-center justify-center">
                        <FontAwesomeIcon className="absolute right-0 text-md transition duration-300" icon={ selModel === i ? faChevronUp : faChevronDown } />
                    </div>
                </div>
                { selModel === i ? 
                    <motion.p className="text-left text-sm text-grey-400"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        { data["description"][lang] }
                    </motion.p> 
                    : 
                    <motion.p className="text-left text-sm text-grey-400"
                        initial={{ opacity: 1 }}
                        animate={{ opacity: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        { data["description"][lang] }
                    </motion.p> 
                }
            </motion.div>
        )
    });

    function CreateImageSlides() {
        if (selModel) {
            let data = typedModels[selModel];
            let imageSrc = data["image"];
            let sketchSrc = data["sketch"];
            return (
                <motion.div className="grid grid-flow-col auto-cols-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                >
                    <ImageImport image={sketchSrc} imagePath="image/robotics/" />
                    <ImageImport image={imageSrc} imagePath="image/robotics/" />
                </motion.div>
            )
        } else {
            return (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                    <img src={kioskGroup} />
                </motion.div>
            )
        };
    };

    return (
        <div className="relative w-full lg:h-screen sm:h-[1000px] bg-cover bg-center bg-white overflow-hidden">
            <div className="absolute w-[700px] h-[700px] rounded-full blur-3xl filter opacity-60 top-[25%] left-[50%] z-10" style={{ backgroundColor: "#57C4CB" }}/>

            <div className="absolute w-full z-40">
                <div className="m-auto" style={{ maxWidth: `${maxWidthScreen}px` }}>
                    <div className="h-fit flex flex-col lg:gap-40 sm:gap-10">
                        <TitleSubtitle title="Aiia Models for Different Business Needs" />
                        <div className="lg:grid lg:grid-cols-3 sm:flex sm:flex-col gap-8">
                            <div className="col-span-1 flex flex-col items-top justify-top gap-8 lg:px-0 sm:px-8">
                                { sideBar }
                            </div>
                            <div className="col-span-2 lg:px-0 sm:px-8">
                                <CreateImageSlides />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};